import { DomainError, SyncError, TransportError, } from "@econ-v1/domain";
import { deserializeRpcRequest, isJsonValue, serializeRpcEvent, serializeRpcResponse, } from "@econ-v1/rpc";
import { KernelEventBus } from "../bus/event-bus.js";
import { CapabilityAuthorization } from "../capabilities/authorization.js";
export class KernelRpcServer {
    #authorization;
    #bus;
    #capabilities;
    #commands;
    #liveQueries;
    #stages;
    #nextSessionId = 1;
    constructor(dependencies) {
        this.#authorization = dependencies.authorization;
        this.#bus = dependencies.bus;
        this.#capabilities = dependencies.capabilities;
        this.#commands = dependencies.commands ?? {
            execute: async () => {
                throw new DomainError({ code: "not_found", message: "Host command is not registered" });
            },
        };
        this.#liveQueries = dependencies.liveQueries;
        this.#stages = dependencies.stages;
    }
    attach(transport) {
        const state = {
            cancelledSubscriptions: new Set(),
            closed: false,
            pendingSubscriptions: new Map(),
            sessions: new Map(),
            subscriptions: new Map(),
            transport,
            unsubscribeClose: () => undefined,
            unsubscribeMessages: () => undefined,
        };
        state.unsubscribeMessages = transport.subscribe((message) => {
            void this.#handleMessage(state, message);
        });
        state.unsubscribeClose = transport.subscribeClose(() => this.#cleanup(state));
        return () => this.#cleanup(state);
    }
    async #dispatch(state, request) {
        switch (request.type) {
            case "command":
                return await this.#commands.execute(request.name, request.payload);
            case "query":
                return jsonValue(await this.#liveQueries.query(request.name, request.params));
            case "live-query.subscribe":
                this.#assertNewSubscription(state, request.subscriptionId);
                await this.#subscribeLiveQuery(state, request.subscriptionId, undefined, request.name, request.params);
                return null;
            case "live-query.unsubscribe":
                this.#unsubscribe(state, request.subscriptionId, undefined);
                return null;
            case "event.subscribe":
                this.#assertNewSubscription(state, request.subscriptionId);
                this.#subscribeEvent(state, request.subscriptionId, undefined, request.eventType);
                return null;
            case "event.unsubscribe":
                this.#unsubscribe(state, request.subscriptionId, undefined);
                return null;
            case "event.publish":
                this.#bus.publish(request.event);
                return null;
            case "stage.prepare":
                return jsonValue(await this.#stages.prepareStage(request.appName));
            case "stage.open-session": {
                const context = await this.#stages.bindSession(request.appName, request.manifestSha256);
                const sessionId = `session-${this.#nextSessionId}`;
                this.#nextSessionId += 1;
                state.sessions.set(sessionId, context);
                return { sessionId };
            }
            case "stage.query": {
                const context = this.#session(state, request.sessionId);
                this.#authorization.assertQueryDeclared(context, request.name);
                return jsonValue(await this.#liveQueries.query(request.name, request.params));
            }
            case "stage.live-query.subscribe": {
                const context = this.#session(state, request.sessionId);
                this.#authorization.assertQueryDeclared(context, request.name);
                this.#assertNewSubscription(state, request.subscriptionId);
                await this.#subscribeLiveQuery(state, request.subscriptionId, request.sessionId, request.name, request.params);
                return null;
            }
            case "stage.live-query.unsubscribe":
                this.#session(state, request.sessionId);
                this.#unsubscribe(state, request.subscriptionId, request.sessionId);
                return null;
            case "stage.event.subscribe": {
                const context = this.#session(state, request.sessionId);
                this.#authorization.assertEventDeclared(context, request.eventType);
                this.#assertNewSubscription(state, request.subscriptionId);
                this.#subscribeEvent(state, request.subscriptionId, request.sessionId, request.eventType);
                return null;
            }
            case "stage.event.unsubscribe":
                this.#session(state, request.sessionId);
                this.#unsubscribe(state, request.subscriptionId, request.sessionId);
                return null;
            case "stage.event.publish": {
                const context = this.#session(state, request.sessionId);
                this.#authorization.assertEventDeclared(context, request.event.type);
                this.#bus.publish(request.event);
                return null;
            }
            case "stage.capability.invoke": {
                const context = this.#session(state, request.sessionId);
                this.#authorization.assertCapabilityDeclared(context, request.capability);
                return jsonValue(await this.#capabilities.invoke(context, request.capability, request.payload));
            }
            case "stage.close-session":
                this.#session(state, request.sessionId);
                this.#closeSession(state, request.sessionId);
                return null;
        }
    }
    async #handleMessage(state, message) {
        let request;
        try {
            request = deserializeRpcRequest(message);
        }
        catch {
            return;
        }
        try {
            const value = await this.#dispatch(state, request);
            if (!state.closed)
                state.transport.send(serializeRpcResponse({ id: request.id, ok: true, value }));
        }
        catch (error) {
            if (!state.closed) {
                state.transport.send(serializeRpcResponse({ error: clientError(error), id: request.id, ok: false }));
            }
        }
    }
    #assertNewSubscription(state, subscriptionId) {
        if (state.subscriptions.has(subscriptionId) || state.pendingSubscriptions.has(subscriptionId)) {
            throw new DomainError({ code: "conflict", message: `Subscription ${subscriptionId} already exists` });
        }
    }
    #cleanup(state) {
        if (state.closed)
            return;
        state.closed = true;
        state.unsubscribeMessages();
        state.unsubscribeClose();
        for (const subscription of state.subscriptions.values())
            subscription.unsubscribe();
        state.subscriptions.clear();
        state.pendingSubscriptions.clear();
        state.cancelledSubscriptions.clear();
        state.sessions.clear();
    }
    #closeSession(state, sessionId) {
        for (const [subscriptionId, subscription] of [...state.subscriptions]) {
            if (subscription.sessionId === sessionId) {
                subscription.unsubscribe();
                state.subscriptions.delete(subscriptionId);
            }
        }
        for (const [subscriptionId, pendingSessionId] of state.pendingSubscriptions) {
            if (pendingSessionId === sessionId)
                state.cancelledSubscriptions.add(subscriptionId);
        }
        state.sessions.delete(sessionId);
    }
    #session(state, sessionId) {
        const context = state.sessions.get(sessionId);
        if (context !== undefined)
            return context;
        throw new DomainError({ code: "unauthorized", message: "Unknown or closed stage session" });
    }
    #subscribeEvent(state, subscriptionId, sessionId, eventType) {
        const unsubscribe = this.#bus.subscribe(eventType, (event) => {
            if (!state.closed) {
                state.transport.send(serializeRpcEvent({ event: event, subscriptionId, type: "kernel.event" }));
            }
        });
        state.subscriptions.set(subscriptionId, { sessionId, unsubscribe });
    }
    async #subscribeLiveQuery(state, subscriptionId, sessionId, name, params) {
        state.pendingSubscriptions.set(subscriptionId, sessionId);
        let engineSubscriptionId;
        try {
            engineSubscriptionId = await this.#liveQueries.subscribe(name, params, (diff) => {
                if (!state.closed) {
                    state.transport.send(serializeRpcEvent({ diff, subscriptionId, type: "live-query.changed" }));
                }
            });
        }
        finally {
            state.pendingSubscriptions.delete(subscriptionId);
        }
        if (state.closed || state.cancelledSubscriptions.delete(subscriptionId)) {
            this.#liveQueries.unsubscribe(engineSubscriptionId);
            return;
        }
        state.subscriptions.set(subscriptionId, {
            sessionId,
            unsubscribe: () => this.#liveQueries.unsubscribe(engineSubscriptionId),
        });
    }
    #unsubscribe(state, subscriptionId, sessionId) {
        if (state.pendingSubscriptions.get(subscriptionId) === sessionId) {
            state.cancelledSubscriptions.add(subscriptionId);
            return;
        }
        const subscription = state.subscriptions.get(subscriptionId);
        if (subscription === undefined || subscription.sessionId !== sessionId) {
            throw new DomainError({ code: "not_found", message: `Subscription ${subscriptionId} was not found` });
        }
        subscription.unsubscribe();
        state.subscriptions.delete(subscriptionId);
    }
}
function clientError(error) {
    if (error instanceof DomainError || error instanceof SyncError || error instanceof TransportError)
        return error;
    const reason = error instanceof Error ? error.name : typeof error;
    return new DomainError({
        code: "invalid_request",
        details: { reason },
        message: `Kernel RPC request failed (unexpected ${reason})`,
    });
}
function jsonValue(value) {
    if (isJsonValue(value))
        return value;
    throw new DomainError({ code: "invalid_request", message: "Kernel RPC result is not JSON-serializable" });
}
//# sourceMappingURL=kernel-rpc-server.js.map